"use client";

import { getLetterhead, letterheadModel } from "@/lib/invoice/letters";
import { tint } from "@/lib/invoice/templates";
import { useEffect, useState } from "react";
import { fmtDate } from "@/lib/invoice/utils";

const INK = "#111827";

// On-screen A4 letter on letterhead; mirrors lib/invoice/letterPdf.js.
// `letter` may be null for a blank letterhead (gallery thumbnails, stationery).
function useQr(text) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    if (!text) return setSrc("");
    import("qrcode").then((QR) => QR.toDataURL(text, { margin: 1, width: 240 }).then((u) => alive && setSrc(u)));
    return () => {
      alive = false;
    };
  }, [text]);
  return src;
}

export default function LetterPreview({ letter, brand, design: designProp, dateStyle = "pretty", thumbnail }) {
  const qr = useQr(!thumbnail && letter && letter.showQr !== false ? brand?.qrLink : "");
  const design = designProp || letter?.design || brand?.letterhead || "classic";
  const lh = getLetterhead(design);
  const b = letterheadModel(brand);
  const accent = b.accent;
  const soft = tint(accent, 0.82);
  const pale = tint(accent, 0.92);
  const light = tint(accent, 0.45);
  const serif = lh.font === "times";
  const contact = b.contact.join("   |   ");
  const pad = design === "sidebar" ? "pl-[91px] pr-[68px]" : "px-[68px]";
  const Logo = ({ className, tile }) =>
    b.logo ? (
      tile ? (
        <div className="shrink-0 rounded-[10px] bg-white p-[8px]">
          <img src={b.logo} alt="" className={className} />
        </div>
      ) : (
        <img src={b.logo} alt="" className={`shrink-0 object-contain ${className}`} />
      )
    ) : null;

  let header;
  let footer;
  let decor = null;

  if (design === "executive") {
    header = (
      <>
        <div className="flex items-center justify-between gap-6 px-[68px] py-[34px] text-white" style={{ background: INK }}>
          <div className="flex items-center gap-5">
            <Logo tile className="h-[60px] w-[60px] object-contain" />
            <div>
              <div className="text-[27px] font-bold leading-tight">{b.name}</div>
              {b.tagline && (
                <div className="mt-1 text-[10px] uppercase tracking-[0.2em]" style={{ color: light }}>
                  {b.tagline}
                </div>
              )}
            </div>
          </div>
          <div className="max-w-[300px] text-right text-[10.5px] leading-relaxed text-white/75">
            {[b.address, b.phone && `Tel: ${b.phone}`, b.email, b.website].filter(Boolean).map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        </div>
        <div className="h-[6px]" style={{ background: accent }} />
      </>
    );
    footer = (
      <>
        <div className="pb-[14px] text-center text-[10px] text-neutral-500">{b.ids.join("   ·   ")}</div>
        <div className="h-[3px]" style={{ background: accent }} />
        <div className="h-[11px]" style={{ background: INK }} />
      </>
    );
  } else if (design === "wave") {
    decor = (
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 210 297" preserveAspectRatio="none" aria-hidden="true">
        <polygon points="110,0 210,0 210,60" fill={soft} />
        <polygon points="146,0 210,0 210,38" fill={accent} />
        <polygon points="0,255 0,297 88,297" fill={soft} />
        <polygon points="0,271 0,297 56,297" fill={accent} />
      </svg>
    );
    header = (
      <div className="relative px-[68px] pt-[48px]">
        <div className="flex items-center gap-4">
          <Logo className="h-[68px] w-[68px]" />
          <div>
            <div className="text-[28px] font-bold leading-tight text-neutral-900">{b.name}</div>
            {b.tagline && <div className="text-[12px] italic text-neutral-500">{b.tagline}</div>}
          </div>
        </div>
        <div className="mt-3 max-w-[480px] text-[11px] leading-relaxed text-neutral-600">
          <div>{b.address}</div>
          {contact && <div>{contact}</div>}
        </div>
        <div className="mt-2 h-[5px] w-[83px]" style={{ background: accent }} />
      </div>
    );
    footer = (
      <div className="relative px-[68px] pb-[26px] text-right text-[10.5px] leading-relaxed text-neutral-500">
        {b.ids.length > 0 && <div>{b.ids.join("   ·   ")}</div>}
        {b.website && <div>{b.website}</div>}
      </div>
    );
  } else if (design === "sidebar") {
    decor = (
      <>
        <div className="absolute inset-y-0 left-0 w-[26px]" style={{ background: accent }} />
        <div className="absolute inset-y-0 left-[26px] w-[8px]" style={{ background: soft }} />
      </>
    );
    header = (
      <div className="relative pl-[91px] pr-[68px] pt-[44px]">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <Logo className="h-[68px] w-[68px]" />
            <div>
              <div className="text-[27px] font-bold leading-tight" style={{ color: accent }}>
                {b.name}
              </div>
              {b.tagline && <div className="text-[11.5px] text-neutral-500">{b.tagline}</div>}
            </div>
          </div>
          <div className="text-right text-[10.5px] leading-relaxed text-neutral-600">
            {[b.phone && `Tel: ${b.phone}`, b.email, b.website].filter(Boolean).map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        </div>
        <div className="mt-4 border-t border-neutral-300" />
      </div>
    );
    footer = (
      <div className="relative ml-[34px] flex items-start justify-between gap-6 py-[18px] pl-[57px] pr-[68px] text-[10.5px]" style={{ background: pale }}>
        <div className="max-w-[350px] text-neutral-800">{b.address}</div>
        <div className="text-right text-neutral-500">
          {b.ids.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      </div>
    );
  } else if (design === "royal") {
    header = (
      <div className="px-[68px] pt-[40px] text-center">
        {b.logo && <img src={b.logo} alt="" className="mx-auto mb-3 h-[68px] w-[68px] object-contain" />}
        <div className="text-[28px] font-bold uppercase tracking-[0.12em]" style={{ color: accent }}>
          {b.name}
        </div>
        {b.tagline && <div className="text-[13px] italic text-neutral-500">{b.tagline}</div>}
        <div className="mt-1 text-[11px] text-neutral-600">{[b.address, ...b.contact].filter(Boolean).join("  ·  ")}</div>
        <div className="mt-3 border-t-[2.5px]" style={{ borderColor: accent }} />
        <div className="mt-[3px] border-t" style={{ borderColor: accent }} />
      </div>
    );
    footer = (
      <div className="px-[68px] pb-[30px] text-center">
        <div className="border-t" style={{ borderColor: accent }} />
        <div className="mt-[3px] border-t-[2.5px]" style={{ borderColor: accent }} />
        <div className="mt-2 text-[10.5px] italic text-neutral-500">{b.ids.join("   ·   ") || b.website}</div>
      </div>
    );
  } else if (design === "corporate") {
    header = (
      <div className="px-[68px] pt-[44px]">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <Logo className="h-[76px] w-[76px]" />
            <div>
              <div className="text-[27px] font-bold leading-tight" style={{ color: INK }}>
                {b.name}
              </div>
              {b.tagline && (
                <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: accent }}>
                  {b.tagline}
                </div>
              )}
            </div>
          </div>
          <table className="text-[11px]">
            <tbody>
              {[["PHONE", b.phone], ["EMAIL", b.email], ["WEB", b.website]]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <tr key={k}>
                    <td className="pr-2 text-right text-[9px] font-bold" style={{ color: accent }}>
                      {k}
                    </td>
                    <td className="text-right text-neutral-800">{v}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex gap-[4px]">
          <div className="h-[8px] w-[72%]" style={{ background: accent }} />
          <div className="h-[8px] flex-1" style={{ background: INK }} />
        </div>
      </div>
    );
    footer = (
      <div className="border-t-[3px] py-[12px] text-center text-[10.5px]" style={{ background: pale, borderColor: accent }}>
        <div className="text-neutral-800">{b.address}</div>
        {b.ids.length > 0 && <div className="text-neutral-500">{b.ids.join("   ·   ")}</div>}
      </div>
    );
  } else if (design === "minimal") {
    header = (
      <div className="px-[68px] pt-[44px]">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[24px] font-bold text-neutral-900">{b.name}</div>
            {b.tagline && <div className="text-[11.5px] text-neutral-500">{b.tagline}</div>}
          </div>
          <div className="max-w-[300px] text-right text-[10.5px] leading-relaxed text-neutral-500">
            <div>{b.address}</div>
            {b.contact.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        </div>
        <div className="mt-3 border-t border-neutral-700" />
      </div>
    );
    footer = <div className="px-[68px] pb-[30px] text-center text-[10px] text-neutral-500">{b.ids.join("   ·   ")}</div>;
  } else if (design === "centered") {
    header = (
      <div className="px-[68px] pt-[40px] text-center">
        {b.logo && <img src={b.logo} alt="" className="mx-auto mb-2 h-[60px] w-[60px] object-contain" />}
        <div className="text-[24px] font-bold text-neutral-900">{b.name}</div>
        {b.tagline && <div className="text-[11.5px] text-neutral-500">{b.tagline}</div>}
        <div className="text-[10.5px] text-neutral-600">{b.address}</div>
        {contact && <div className="text-[10.5px] text-neutral-600">{contact}</div>}
        <div className="mt-2 border-t-2" style={{ borderColor: accent }} />
      </div>
    );
    footer = (
      <div className="px-[68px] pb-[28px] text-center text-[10px] text-neutral-500">
        <div className="mb-2 border-t border-neutral-300" />
        {[b.ids.join("   ·   "), b.website].filter(Boolean).join("   ·   ")}
      </div>
    );
  } else {
    header = (
      <div className="px-[68px] pt-[44px]">
        <div className="flex items-center gap-4">
          <Logo className="h-[76px] w-[76px]" />
          <div>
            <div className="text-[28px] font-bold leading-tight" style={{ color: accent }}>
              {b.name}
            </div>
            {[b.tagline, b.ids.join("   ·   ")].filter(Boolean).map((l) => (
              <div key={l} className="text-[11.5px] text-neutral-600">
                {l}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 h-[5px]" style={{ background: accent }} />
      </div>
    );
    footer = (
      <div className="px-[68px] pb-[26px] text-center text-[10.5px]">
        <div className="mb-2 h-[2px]" style={{ background: accent }} />
        <div className="text-neutral-800">{b.address}</div>
        {contact && <div className="text-neutral-500">{contact}</div>}
      </div>
    );
  }

  const L = letter;
  const to = L?.to || {};
  const paragraphs = String(L?.body || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const sigImg = L && L.showSignature !== false && b.signature;
  const stampImg = L && (L.showStamp ?? brand?.stampOnLetters !== false) && brand?.stamp;

  return (
    <div
      className={
        thumbnail
          ? "paper-solid relative flex h-[1123px] w-[794px] flex-col overflow-hidden bg-white text-neutral-900"
          : "paper-solid relative mx-auto flex min-h-[1123px] w-[794px] flex-col overflow-hidden rounded-sm bg-white text-neutral-900 shadow-lift ring-1 ring-slate-900/5 print:shadow-none print:ring-0"
      }
      style={{ fontFamily: serif ? "'Times New Roman', Times, serif" : undefined }}
      data-testid="letter-preview"
    >
      {decor}
      {L?.watermark && b.logo && <img src={b.logo} alt="" className="pointer-events-none absolute left-1/2 top-1/2 w-[416px] -translate-x-1/2 -translate-y-1/2 object-contain opacity-[0.045]" />}
      <div className="relative">{header}</div>
      <div className={`relative flex-1 ${pad} pb-10 pt-[44px] text-[13.3px] leading-[1.55]`}>
        {L && (
          <>
            <div className="flex justify-between font-bold">
              <span>{L.number ? `Ref: ${L.number}` : ""}</span>
              <span>Date: {fmtDate(L.date, dateStyle)}</span>
            </div>
            {(to.name || to.lines) && (
              <div className="mt-6">
                <div>To,</div>
                {to.name && <div className="font-bold">{to.name}</div>}
                {to.lines && <div className="whitespace-pre-line text-neutral-700">{to.lines}</div>}
                {to.attention && <div className="text-neutral-700">Kind attn: {to.attention}</div>}
              </div>
            )}
            {L.subject && (
              <div className="mt-5 flex gap-1.5 font-bold">
                <span>Subject:</span>
                <span>{L.subject}</span>
              </div>
            )}
            {L.salutation && <div className="mt-5">{L.salutation}</div>}
            {paragraphs.map((p, i) => (
              <p key={i} className="mt-3 whitespace-pre-line">
                {p}
              </p>
            ))}
            <div className="relative mt-7 flex break-inside-avoid items-start justify-between gap-6">
              <div className="relative">
                {L.closing && <div>{L.closing}</div>}
                <div className="font-bold">For {b.name}</div>
                {sigImg ? <img src={b.signature} alt="Signature" className="my-1 h-[50px] object-contain" /> : <div className="h-[42px]" />}
                <div className="font-bold">{L.signName || b.signatory}</div>
                {L.signTitle && <div className="text-neutral-500">{L.signTitle}</div>}
                {stampImg && <img src={brand.stamp} alt="Stamp" className="absolute left-[196px] top-1 h-[90px] w-[90px] object-contain" />}
              </div>
              {qr && (
                <div className="w-[96px] text-center text-[10px] text-neutral-500">
                  <img src={qr} alt="QR code" className="h-[90px] w-[90px]" />
                  {brand.qrLabel}
                </div>
              )}
            </div>
            {(L.enclosures || L.cc) && (
              <div className="mt-6 text-[12px] text-neutral-500">
                {L.enclosures && <div>Encl: {L.enclosures}</div>}
                {L.cc && <div>CC: {L.cc}</div>}
              </div>
            )}
          </>
        )}
      </div>
      <div className="relative">{footer}</div>
    </div>
  );
}
